/**
 * Состояние витрины · Ф2.2 — ровно то, что уже отдало API.
 * ⛔ Ничего не считаем и не кешируем «на будущее»: правда живёт в ядре,
 * витрина её только показывает.
 */

import type { AchievementsOut, AdminOut, FeedbackOut, MeOut, ProgressOut,
              ResetOut, Settings } from "./api";
import { feedbackState as apiFeedback, leaderboard as apiLeaderboard, patchSettings,
         rateApp, resetScope, resetState, resetUndo, setCharacter as apiSetCharacter,
         sendTicket, skipRating } from "./api";
import type { LbOut } from "./leaderboard";
import { EMPTY as XP_EMPTY, type XpState } from "./xp";
import { type AudioPrefs, prefs as audioPrefs, prefsToSettings, readPrefs, setPrefs } from "./audio";
import { applyTheme, type Theme, type ThemePref } from "./tg";

// ⚠ «admin» — экран БЕЗ пункта навигации: нижних кнопок по-прежнему пять
// (решение дизайн-ворот), а сюда ведёт карточка в настройках у тех, кому можно.
export type ScreenName = "cabinet" | "stats" | "session" | "awards" | "settings"
                       | "admin" | "danger" | "library";

export interface AppState {
  me: MeOut | null;
  progress: ProgressOut | null;
  /** Ф2.4: каталог наград с прогрессом — считает ядро, витрина только раскладывает. */
  achievements: AchievementsOut | null;
  /** Окно 4: таблицы участников — считает ядро, витрина только раскладывает. */
  leaderboard: LbOut | null;
  /** Окно 5: спрашивать ли оценку + мои заявки со статусами (всё считает ядро). */
  feedback: FeedbackOut | null;
  /** Окно 5: сводка владельца. У постороннего остаётся null — API отвечает 403. */
  admin: AdminOut | null;
  /** Окно 6: «Опасная зона» — что потеряется, чем подтвердить, чем вернуть. */
  reset: ResetOut | null;
  screen: ScreenName;
  themePref: ThemePref;
  theme: Theme;
}

export const state: AppState = {
  me: null,
  progress: null,
  achievements: null,
  leaderboard: null,
  feedback: null,
  admin: null,
  reset: null,
  screen: "cabinet",
  themePref: "light",     // светлая по умолчанию (дизайн-ворота 24.07)
  theme: "light",
};

function isPref(value: unknown): value is ThemePref {
  return value === "light" || value === "dark" || value === "auto";
}

/** Тема и звук из настроек пользователя (пер-юзерные строки в settings). */
export function adoptSettings(settings: Settings): void {
  const pref = settings.theme;
  state.themePref = isPref(pref) ? pref : "light";
  state.theme = applyTheme(state.themePref);
  setPrefs(readPrefs(settings as Record<string, unknown>));
}

/**
 * Сменить настройку звука: сразу в плеере (скорость слышна на текущей записи),
 * потом тихо сохранить у себя. Пишем ровно то, что изменилось.
 */
export async function setAudioPrefs(patch: Partial<AudioPrefs>): Promise<void> {
  const before = prefsToSettings(audioPrefs());
  const next = { ...audioPrefs(), ...patch };
  setPrefs(next);
  const after = prefsToSettings(next);
  // ⚠ Пишем ТОЛЬКО изменившийся ключ. Тройкой нельзя: два быстрых нажатия подряд — и
  // ответ на первое, придя вторым, вернёт настройку, которую человек уже переключил
  // (поймано живьём: «Выкл» откатывался в «Слово» запоздавшей записью скорости).
  const changes: Record<string, string | number> = {};
  for (const key of Object.keys(after)) {
    if (after[key] !== before[key]) changes[key] = after[key];
  }
  if (!Object.keys(changes).length) return;
  if (state.me) Object.assign(state.me.settings, changes);
  try {
    await patchSettings(changes);
  } catch {
    /* не сохранилось — на экране настройка уже стоит, повторим при следующей смене */
  }
}

/** Сменить тему: сразу на экране, потом тихо сохранить у себя в настройках. */
export async function setThemePref(pref: ThemePref): Promise<void> {
  state.themePref = pref;
  state.theme = applyTheme(pref);
  if (state.me) state.me.settings.theme = pref;
  try {
    await patchSettings({ theme: pref });
  } catch {
    /* не сохранилось — на экране тема уже стоит, повторим при следующей смене */
  }
}

/**
 * Опыт и уровень — ровно то, что прислало ядро. Своего расчёта у витрины нет:
 * пока `/me` не ответил (или слой 3 в базе не заведён), показываем честный ноль.
 */
export function xp(): XpState {
  return state.me?.xp ?? XP_EMPTY;
}

/**
 * Ф2.5: сменить героя. Сразу на экране (человек видит выбор мгновенно), потом запись;
 * не сохранилось — возвращаем прежнего, чтобы экран не врал о том, чего нет в базе.
 */
export async function setCharacterKey(key: string): Promise<void> {
  const was = state.me?.xp?.character;
  if (state.me?.xp) state.me.xp.character = key;
  try {
    const res = await apiSetCharacter(key);
    if (state.me) state.me.xp = res.xp;
  } catch {
    if (state.me?.xp && was) state.me.xp.character = was;
  }
}

/**
 * Окно 4: согласие показывать имя другим. Сразу на экране (человек видит решение
 * мгновенно), потом запись — и СРАЗУ перечитываем таблицу: имя в строках рисует ядро,
 * витрина сама ничьё имя не подставляет. Не сохранилось — возвращаем как было, чтобы
 * экран не обещал того, чего в базе нет.
 */
/**
 * ⭐ Окно 3 «трейдинг»: включить/выключить доменный набор.
 *
 * ⛔ Витрина только шлёт решение: сам набор включает ядро (`seed_data.set_trading`
 * правит `subgroups.is_active`), и очередь занятия смотрит именно туда. Экран
 * обновляем сразу, чтобы тумблер не «отставал», а расхождение чиним ответом API.
 */
export async function setTradingEnabled(on: boolean): Promise<void> {
  const me = state.me as any;
  if (me?.trading) { me.trading.enabled = on; me.trading.asked = true; }
  if (me?.settings) me.settings.trading_enabled = on ? 1 : 0;
  try {
    const res: any = await patchSettings({ trading_enabled: on ? 1 : 0 });
    if (me && res?.trading) me.trading = { ...me.trading, ...res.trading, asked: true };
  } catch {
    if (me?.trading) me.trading.enabled = !on;      // не доехало — вернули как было
    throw new Error("Не удалось изменить набор");
  }
}


/**
 * ⭐ 21.08: включить/выключить ЛЮБОЙ тематический набор из реестра ядра.
 *
 * ⛔ Витрина по-прежнему ничего не решает: включает набор ядро (`packs.set_pack`), и
 * у взрослого набора оно же отобьёт включение без подтверждения возраста — экран
 * спрашивает про 18+ заранее только ради вежливости, а не вместо правила.
 */
export async function setPackEnabled(key: string, setting: string, on: boolean): Promise<void> {
  const me = state.me as any;
  const list: any[] = me?.packs?.list || [];
  const item = list.find((x) => x.key === key);
  if (item) { item.enabled = on; item.asked = true; }
  try {
    const res: any = await patchSettings({ [setting]: on ? 1 : 0 });
    const fresh = res?.packs?.[key];
    if (item && fresh) Object.assign(item, fresh, { asked: true });
  } catch (e) {
    if (item) item.enabled = !on;                   // не доехало — вернули как было
    throw e instanceof Error ? e : new Error("Не удалось изменить набор");
  }
}

/** Подтверждение возраста. ⭐ Хранит ЯДРО — это ответ человека, а не флаг экрана. */
export async function confirmAdult(): Promise<void> {
  const me = state.me as any;
  await patchSettings({ adult_confirmed: 1 });
  if (me?.packs) me.packs.adult_confirmed = true;
}


export async function setStatsOptIn(on: boolean): Promise<void> {
  const was = state.leaderboard
    ? { opt_in: state.leaderboard.opt_in, asked: state.leaderboard.asked }
    : null;
  if (state.leaderboard) {
    state.leaderboard.opt_in = on;
    state.leaderboard.asked = true;         // ответил — второй раз не спрашиваем
  }
  if (state.me) state.me.settings.stats_opt_in = on ? 1 : 0;
  try {
    await patchSettings({ stats_opt_in: on ? 1 : 0 });
    state.leaderboard = await apiLeaderboard();
  } catch {
    if (was && state.leaderboard) Object.assign(state.leaderboard, was);
    if (state.me) state.me.settings.stats_opt_in = was?.opt_in ? 1 : 0;
  }
}

/* ── обратная связь (окно 5) ───────────────────────────────────────────────── */

/** Подтянуть состояние обратной связи. Молча: карточка спроса появится сама. */
export async function loadFeedback(): Promise<void> {
  try {
    state.feedback = await apiFeedback();
  } catch {
    /* не доехало — просто не спрашиваем сегодня, это не повод пугать человека */
  }
}

/**
 * Оценка ★1–5 с пожеланием. ⭐ На экране закрываем карточку СРАЗУ: человек ответил,
 * и держать вопрос на месте до ответа сервера — значит намекать, что его не услышали.
 */
export async function rate(stars: number, text?: string): Promise<void> {
  if (state.feedback) state.feedback.ask = { ...state.feedback.ask, ask: false, why: "оценил" };
  if (state.me?.feedback) state.me.feedback.ask = false;
  try {
    await rateApp(stars, text);
  } catch {
    /* не сохранилось — переспросим в следующий заход, а не сейчас поверх «спасибо» */
  }
}

/** «Не сейчас». Отказ — это ответ: записываем его, чтобы не приставать неделю. */
export async function rateLater(): Promise<void> {
  if (state.feedback) state.feedback.ask = { ...state.feedback.ask, ask: false, why: "не сейчас" };
  if (state.me?.feedback) state.me.feedback.ask = false;
  try {
    await skipRating();
  } catch { /* см. выше */ }
}

/** Заявка «не работает» + контекст, собранный приложением. Ошибку показываем честно. */
export async function reportProblem(text: string,
                                    context: Record<string, string>): Promise<boolean> {
  try {
    const ticket = await sendTicket(text, context);
    if (state.feedback) state.feedback.my = [ticket, ...state.feedback.my];
    return true;
  } catch {
    return false;
  }
}

/* ── «начать с нуля» (окно 6) ─────────────────────────────────────────────── */

/**
 * Подтянуть состояние опасной зоны. ⭐ Цифры «что потеряешь», фразу подтверждения и
 * срок отката считает и хранит ЯДРО — витрина ничего из этого не выдумывает.
 */
export async function loadReset(): Promise<void> {
  try {
    state.reset = await resetState();
  } catch {
    /* не доехало — экран честно скажет, что цифры не пришли, и кнопки не даст */
  }
}

/**
 * Сбросить один вид прогресса. Возвращает подпись «что сделали» или null при отказе.
 * ⭐ Слово проверяет и витрина, и ядро: спрятанная кнопка защитой не является.
 * ⛔ После сброса ПЕРЕЧИТЫВАЕМ всё: опыт, награды и словарь на других экранах должны
 * стать честными сразу, а не после перезахода.
 */
export async function doReset(scope: string, confirm: string): Promise<string | null> {
  try {
    const res = await resetScope(scope, confirm);
    state.reset = res.state;
    return res.title;
  } catch {
    return null;
  }
}

/** «Вернуть как было». null = не получилось (снимок протух или его уже использовали). */
export async function doUndo(snapshotId?: number): Promise<string | null> {
  try {
    const res = await resetUndo(snapshotId);
    state.reset = res.state;
    return res.title;
  } catch {
    return null;
  }
}

/** Перерисовать по текущей настройке (сменилась тема в самом Telegram). */
export function reapplyTheme(): void {
  state.theme = applyTheme(state.themePref);
}
